import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, Maximize2, Loader2, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function StaffAttendanceWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      // Get today's attendance summary
      const result = await apiRequest('/kiosk/timesheet/today-summary');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch attendance');
      setData({ clocked_in: 0, clocked_out: 0, total_expected: 0, late: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="staff-attendance-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-green-500" />
            <CardTitle className="text-lg">Staff Attendance</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/admin/timesheet')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Today's Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 bg-green-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-lg font-bold text-green-700">{data?.clocked_in || 0}</span>
                </div>
                <p className="text-xs text-green-600">Clocked In</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="text-lg font-bold text-blue-700">{data?.clocked_out || 0}</span>
                </div>
                <p className="text-xs text-blue-600">Clocked Out</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <UserCheck className="h-4 w-4 text-slate-600" />
                  <span className="text-lg font-bold text-slate-700">{data?.total_expected || 0}</span>
                </div>
                <p className="text-xs text-slate-600">Expected</p>
              </div>
            </div>

            {/* Late arrivals if any */}
            {data?.late > 0 && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700 text-xs">
                <AlertCircle className="h-4 w-4" />
                <span>{data.late} late arrival{data.late > 1 ? 's' : ''} today</span>
              </div>
            )}

            {/* View All button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => navigate('/admin/timesheet')}
            >
              View Full Timesheet
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
